import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { fetchNotifications, markNotificationRead, markAllNotificationsRead, deleteNotification, clearAllNotifications } from "../../../api/notifications.api";
import { messaging } from "../../../firebase";
import { onMessage } from "firebase/messaging";
import { toast } from "react-toastify";
import { NavMenuToggle } from "../../layouts/nav/NavHader";
import { ThemeContext } from "../../../context/ThemeContext";
import Swal from "sweetalert2";

// Simple relative time formatter
const timeAgo = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const HostHeader = () => {
  const navigate = useNavigate();
  const { menuToggle } = useContext(ThemeContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Fetch data
  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await fetchNotifications(1, 10); // get first 10
      if (data) {
        setNotifications(data.notifications);
        setUnreadCount(data.unreadCount);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();

    // Listen for live foreground notifications
    let unsubscribe = () => { };
    if (messaging) {
      unsubscribe = onMessage(messaging, (payload) => {
        console.log("Foreground Message received in Header:", payload);
        loadNotifications();
      });
    }

    // Also reload when user comes back to the tab (handles background updates)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("Tab is active, reloading notifications...");
        loadNotifications();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (messaging) unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleMarkRead = async (id, e) => {
    e.stopPropagation(); // prevent dropdown close if needed
    try {
      await markNotificationRead(id);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error(err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await deleteNotification(id);
      setNotifications(prev => prev.filter(n => n._id !== id));
      // If it was unread, decrease count
      const wasUnread = notifications.find(n => n._id === id)?.read === false;
      if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error(err);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Are you sure you want to clear all notifications?")) return;
    try {
      await clearAllNotifications();
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <header
      className="d-flex justify-content-between justify-content-lg-end align-items-center px-4 py-3 bg-white border-bottom"
      style={{ minHeight: 64 }}
    >
      <div className="host-mobile-header d-lg-none d-flex align-items-center" style={{ position: 'relative', height: '100%' }}>
        <div
          className="nav-control"
          onClick={() => {
            NavMenuToggle();
          }}
          style={{ display: 'block', padding: 0 }}
        >
          <div className={`hamburger ${menuToggle ? "is-active" : ""}`} style={{ padding: 0 }}>
            <span className="line" style={{ background: '#333' }}></span>
            <span className="line" style={{ background: '#333' }}></span>
            <span className="line" style={{ background: '#333' }}></span>
          </div>
        </div>
      </div>

      <div className="d-flex align-items-center gap-4">

        {/* Notification Dropdown */}
        <Dropdown show={isOpen} onToggle={(val) => setIsOpen(val)} align="end">
          <Dropdown.Toggle
            as="button"
            className="btn btn-link p-0 me-3 border-0 bg-transparent notification-toggle"
            style={{ fontSize: 24, color: "#888", boxShadow: 'none' }}
          >
            <div className="position-relative">
              <i className={`bi bi-bell-fill ${unreadCount > 0 ? "text-primary" : ""}`}></i>
              {unreadCount > 0 && (
                <span
                  className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                  style={{ fontSize: '0.45em', padding: '0.25em 0.4em' }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
          </Dropdown.Toggle>

          <Dropdown.Menu className="shadow border-0 p-0 notification-dropdown-menu" style={{ width: 320, maxHeight: 500, overflow: 'hidden' }}>
            <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom bg-light">
              <h6 className="mb-0 fw-bold">Notifications</h6>
              <div className="d-flex gap-2">
                {unreadCount > 0 && (
                  <button className="btn btn-link btn-sm text-decoration-none p-0" style={{ fontSize: 12 }} onClick={handleMarkAllRead}>
                    Mark all read
                  </button>
                )}
                {notifications.length > 0 && (
                  <button className="btn btn-link btn-sm text-danger text-decoration-none p-0" style={{ fontSize: 12 }} onClick={handleClearAll}>
                    Clear all
                  </button>
                )}
              </div>
            </div>

            <div className="custom-scroll" style={{ maxHeight: 350, overflowY: 'auto' }}>
              {notifications.length === 0 ? (
                <div className="text-center py-4 text-muted small">No notifications</div>
              ) : (
                notifications.map(n => (
                  <div
                    key={n._id}
                    className={`px-3 py-2 border-bottom position-relative hover-bg ${n.read ? 'bg-white' : 'bg-light'}`}
                    style={{ transition: 'background-color 0.2s', borderLeft: n.read ? 'none' : '3px solid #0d6efd' }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="me-2" style={{ flex: 1 }}>
                        <p className={`mb-1 small ${n.read ? 'text-secondary' : 'fw-bold text-dark'}`}>
                          {n.title}
                        </p>
                        <p className="mb-1 text-muted small text-truncate-2" style={{ fontSize: '0.8rem', lineHeight: 1.2 }}>
                          {n.body}
                        </p>
                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>{timeAgo(n.createdAt)}</small>
                      </div>
                      <div className="d-flex flex-column gap-1">
                        <button
                          className="btn btn-link text-danger p-0"
                          title="Remove"
                          onClick={(e) => handleDelete(n._id, e)}
                        >
                          <i className="bi bi-x" style={{ fontSize: 18 }}></i>
                        </button>
                        {!n.read && (
                          <button
                            className="btn btn-link text-primary p-0"
                            title="Mark as read"
                            onClick={(e) => handleMarkRead(n._id, e)}
                          >
                            <i className="bi bi-check-circle" style={{ fontSize: 12 }}></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            {/* Refresh button removed as per request */}
          </Dropdown.Menu>
        </Dropdown>

        <button
          className="btn btn-link p-0"
          style={{ fontSize: 28, color: "#888" }}
          aria-label="Profile"
          onClick={() => navigate("/host/profile")}
        >
          <i className="bi bi-person-circle"></i>
        </button>
        <button
          className="btn btn-link p-0 ms-3"
          style={{ fontSize: 28, color: "#888" }}
          aria-label="Logout"
          onClick={() => {
            Swal.fire({
              title: "Are you sure?",
              text: "Do you really want to logout?",
              icon: "warning",
              showCancelButton: true,
              confirmButtonColor: "#d33",
              cancelButtonColor: "#3085d6",
              confirmButtonText: "Yes, Logout",
            }).then((result) => {
              if (result.isConfirmed) {
                navigate("/host");
              }
            });
          }}
        >
          <i className="bi bi-box-arrow-right"></i>
        </button>
      </div>

      <style>{`
        /* Hide the default caret */
        .notification-toggle::after {
            display: none !important;
        }
        .text-truncate-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }
        .hover-bg:hover {
            background-color: #f8f9fa !important;
        }
        /* Custom scrollbar for dropdown */
        .custom-scroll::-webkit-scrollbar { width: 4px; }
        .custom-scroll::-webkit-scrollbar-track { background: #f1f1f1; }
        .custom-scroll::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }

        @media (max-width: 575px) {
            .notification-dropdown-menu {
                width: 90vw !important;
                margin-right: -20px !important;
            }
        }
      `}</style>
    </header>
  );
};

export default HostHeader;
